/* eslint-disable import/no-anonymous-default-export */
import dayjs from 'dayjs';
import i18n from './Locale/i18n';

type DateFormat = 'DD MMM. YYYY' | 'DD MMMM YYYY' | 'DD.MM.YYYY' | 'HH:mm' | 'DD.MM.YYYY - HH:mm';

class Utils {
  format(e?: Date, format?: DateFormat) {
    if (!e) {
      return ' - ';
    }
    return dayjs(e).locale(i18n.language).format(format ? format : 'DD MMM. YYYY');
  }

  getEnumLabel(data: any[], value?: number): string {
    try {
      return data.find((e) => e.value === value)?.title;
    } catch (e) {
      return '';
    }
  }

  array(count: number) {
    return Array.from(Array(count).keys());
  }

  generateRandomHexColors(n: number): string[] {
    function getRandomHexColor(): string {
      return '#' + Math.floor(Math.random() * 16777215).toString(16);
    }

    const colors: string[] = [];
    for (let i = 0; i < n; i++) {
      colors.push(getRandomHexColor());
    }
    return colors;
  }

  generateRandomColor() {
    return '#' + (((1 << 24) * Math.random()) | 0).toString(16);
  }

  getDaysSinceDate(selectedDate?: Date) {
    if (!selectedDate) {
      return 0;
    }
    let diff = dayjs().diff(dayjs(selectedDate), 'day');
    if (diff === 0) {
      diff = 1;
    }
    return diff;
  }

  delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  compressImage = (
    file: File,
    maxWidth: number,
    maxHeight: number,
    qualityStep: number = 0.1
  ): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        let canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        const compress = (quality: number) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              if (blob.size > 100 * 1024 && quality > 0.1) {
                compress(quality - qualityStep);
              } else {
                resolve(blob);
              }
            },
            'image/jpeg',
            quality
          );
        };

        compress(0.9); // Start compression with high quality
      };
      img.onerror = (error) => {
        reject(error);
      };
    });
  };
  /* ------------------------------------------------------------------ */
  /* Colors                                                              */
  /* ------------------------------------------------------------------ */

  normalizeHex(value?: string, fallback = '#3949d7'): string {
    if (!value) return fallback;
    const trimmed = value.trim();
    if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed;
    if (/^[0-9a-fA-F]{6}$/.test(trimmed)) return `#${trimmed}`;
    return fallback;
  }

  hexToRgb(hex: string) {
    const normalized = this.normalizeHex(hex).slice(1);
    const intVal = parseInt(normalized, 16);
    return {
      r: (intVal >> 16) & 255,
      g: (intVal >> 8) & 255,
      b: intVal & 255,
    };
  }

  rgbaFromHex(hex: string, alpha: number): string {
    const { r, g, b } = this.hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  adjustHexColor(hex: string, percent: number): string {
    const value = this.normalizeHex(hex).replace(/^#/, '');

    const r0 = parseInt(value.substring(0, 2), 16);
    const g0 = parseInt(value.substring(2, 4), 16);
    const b0 = parseInt(value.substring(4, 6), 16);

    const r = Math.round(Math.min(255, Math.max(0, r0 + (r0 * percent) / 100)));
    const g = Math.round(Math.min(255, Math.max(0, g0 + (g0 * percent) / 100)));
    const b = Math.round(Math.min(255, Math.max(0, b0 + (b0 * percent) / 100)));

    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }

  lightenColor(color: string, percent: number): string {
    return this.adjustHexColor(color, Math.abs(percent));
  }
}

export default new Utils();
